import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Sparkles, Save } from 'lucide-react';
import { useApp } from '../context/AppContext';
import AppSidebar from '../components/AppSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { api } from '../lib/api';

const ICON_CHOICES = ['Bot', 'PenLine', 'Megaphone', 'Code2', 'BarChart3', 'Lightbulb', 'ListChecks', 'Languages', 'Rocket', 'Brain', 'Heart', 'Briefcase'];

const BuildAgent = () => {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const { agentId } = useParams();
  const cats = t.agents.categories.slice(1);
  const [form, setForm] = useState({ name: '', desc: '', instructions: '', category: cats[0], icon: 'Bot' });

  useEffect(() => {
    if (!agentId) return;
    api.listAgents().then((all) => {
      const a = all.find((x) => x.id === agentId && !x.is_builtin);
      if (!a) { toast.error(lang === 'fr' ? 'Agent introuvable' : 'Agent not found'); navigate('/agents'); return; }
      setForm({ name: a.name, desc: a.desc === '—' ? '' : a.desc, instructions: a.instructions, category: a.category, icon: a.icon });
    }).catch(() => navigate('/agents'));
    // eslint-disable-next-line
  }, [agentId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error(lang === 'fr' ? 'Donnez un nom à l\u2019agent' : 'Give the agent a name'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, category: form.category, icon: form.icon, desc: form.desc, instructions: form.instructions };
      if (agentId) await api.updateAgent(agentId, payload); else await api.createAgent(payload);
      toast.success(agentId ? (lang === 'fr' ? 'Agent mis à jour !' : 'Agent updated!') : (lang === 'fr' ? 'Agent enregistré !' : 'Agent saved!'));
      navigate('/agents');
    } catch (e) {
      toast.error(e.message || (lang === 'fr' ? 'Enregistrement impossible' : 'Could not save'));
    } finally { setSaving(false); }
  };

  const PreviewIcon = Icons[form.icon] || Icons.Bot;

  return (
    <div className="flex bg-background">
      <AppSidebar conversations={[]} onNewChat={() => navigate('/chat')} />
      <main className="flex-1 h-screen overflow-y-auto min-w-0">
        <div className="h-16 border-b border-border flex items-center px-5 sm:px-8 sticky top-0 bg-background/80 backdrop-blur z-10">
          <h1 className="font-display text-xl font-bold">{agentId ? t.build.editTitle : t.build.title}</h1>
        </div>

        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 grid lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3 space-y-6">
            <p className="text-muted-foreground">{t.build.subtitle}</p>

            <div className="space-y-2">
              <Label>{t.build.name}</Label>
              <Input data-testid="agent-name-input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={t.build.namePh} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label>{t.build.desc}</Label>
              <Input data-testid="agent-desc-input" value={form.desc} onChange={(e) => set('desc', e.target.value)} placeholder={t.build.descPh} className="rounded-xl" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.build.category}</Label>
                <Select value={form.category} onValueChange={(v) => set('category', v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.build.icon}</Label>
                <Select value={form.icon} onValueChange={(v) => set('icon', v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ICON_CHOICES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t.build.instructions}</Label>
              <Textarea data-testid="agent-instructions-input" value={form.instructions} onChange={(e) => set('instructions', e.target.value)} placeholder={t.build.instructionsPh} rows={5} className="rounded-xl resize-none" />
            </div>
            <Button onClick={save} disabled={saving} data-testid="save-agent-btn" className="rounded-xl font-semibold gap-2"><Save className="h-4 w-4" /> {t.build.save}</Button>
          </div>

          {/* Preview */}
          <div className="lg:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t.build.preview}</p>
            <div className="rounded-2xl border border-border bg-card p-6 sticky top-24">
              <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center text-primary-foreground">
                <PreviewIcon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">{form.name || (lang === 'fr' ? 'Nom de l\u2019agent' : 'Agent name')}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{form.desc || t.build.descPh}</p>
              <span className="mt-4 inline-block text-xs px-2.5 py-1 rounded-full bg-secondary text-muted-foreground">{form.category}</span>
              {form.instructions && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground line-clamp-4 leading-relaxed">{form.instructions}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BuildAgent;
